// Persistent optimistic-done store. Survives reloads via localStorage so a
// hard reload mid-tap doesn't cause the task to pop back. Entries auto-expire
// after MAX_AGE_MS so we don't carry stale local state forever.

import { writable } from "svelte/store";

const KEY = "surface:optimistic-done";
const MAX_AGE_MS = 8 * 60 * 60 * 1000; // 8h, mirrors server-side override TTL

type Entry = { ts: number };
type Map = Record<string, Entry>;

function load(): Map {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    return prune(parsed);
  } catch {
    return {};
  }
}

function prune(m: Map): Map {
  const cutoff = Date.now() - MAX_AGE_MS;
  const out: Map = {};
  for (const [k, v] of Object.entries(m)) {
    if (v && typeof v === "object" && typeof v.ts === "number" && v.ts >= cutoff) {
      out[k] = v;
    }
  }
  return out;
}

function save(m: Map) {
  try { localStorage.setItem(KEY, JSON.stringify(m)); } catch {}
}

const initial = load();
save(initial);

export const optimisticDone = writable<Map>(initial);

export function markDone(fingerprint: string) {
  optimisticDone.update((m) => {
    const next = { ...m, [fingerprint]: { ts: Date.now() } };
    save(next);
    return next;
  });
}

export function clearDone(fingerprint: string) {
  optimisticDone.update((m) => {
    if (!(fingerprint in m)) return m;
    const next = { ...m };
    delete next[fingerprint];
    save(next);
    return next;
  });
}

// Reconcile against the latest snapshot — purge entries the server now agrees with.
// (i.e. the snapshot already shows state="done" for this fingerprint, so the
// local optimism is no longer needed.)
export function reconcile(confirmedDoneFingerprints: string[]) {
  optimisticDone.update((m) => {
    let changed = false;
    const next = { ...m };
    for (const fp of confirmedDoneFingerprints) {
      if (fp in next) {
        delete next[fp];
        changed = true;
      }
    }
    if (changed) save(next);
    return next;
  });
}

export function isDone(m: Map, fingerprint: string): boolean {
  return fingerprint in m;
}
